<script setup>
import { ref, computed, onBeforeMount } from "vue"
import { RadioGroup, RadioGroupOption } from "@headlessui/vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import OfferCard from "@common/js/components/administration/resale-agreements/common/cards/OfferCard.vue"

const { t } = useI18n()
const offerPrices = ref([])
const loading = ref(true)
const router = useRouter()
const types = [
  { value: "all", label: t("resale-agreements.offers.filters.all") },
  {
    value: "VIRTUAL_BATTERY",
    label: t("resale-agreements.offers.filters.virtual-battery"),
  },
  {
    value: "FEED_IN_TARIFF",
    label: t("resale-agreements.offers.filters.contract"),
  },
]

onBeforeMount(async () => {
  try {
    const response = await axios.get("/resale-agreements/offers")
    offerPrices.value = response.data
  } finally {
    loading.value = false
  }
})

const offers = computed(() => [
  {
    name: t("resale-agreements.offers.cards.edf-oa-1.title"),
    provider: t("resale-agreements.offers.cards.edf-oa-1.provider"),
    logo: "https://agence-energie.com/sites/agence-energie.com/files/images/edf-oa-solaire-logo.jpg",
    id: "edf-oa",
    to: { name: "ProjectPostInstallationResaleAgreementEDFOA" },
    price: t("resale-agreements.offers.cards.edf-oa-1.price"),
    priceType: t("resale-agreements.offers.cards.edf-oa-1.price-type"),
    startingFees: t("resale-agreements.offers.cards.edf-oa-1.starting-fees"),
    additionalFees: t(
      "resale-agreements.offers.cards.edf-oa-1.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.edf-oa-1.description"),
    features: [
      t("resale-agreements.offers.cards.edf-oa-1.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.edf-oa-1.eligibility.system-power"),
      t(
        "resale-agreements.offers.cards.edf-oa-1.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.edf-oa-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: true,
    prices: offerPrices.value.filter((o) =>
      o.categories.includes("EDF_OA_S21")
    ),
    category: "EDF_OA_S21",
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.edf-oa-2.title"),
    provider: t("resale-agreements.offers.cards.edf-oa-2.provider"),
    logo: "https://agence-energie.com/sites/agence-energie.com/files/images/edf-oa-solaire-logo.jpg",
    id: "edf-oa",
    to: { name: "ProjectPostInstallationResaleAgreementEDFOA" },
    price: t("resale-agreements.offers.cards.edf-oa-2.price"),
    priceType: t("resale-agreements.offers.cards.edf-oa-2.price-type"),
    startingFees: t("resale-agreements.offers.cards.edf-oa-2.starting-fees"),
    additionalFees: t(
      "resale-agreements.offers.cards.edf-oa-2.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.edf-oa-2.description"),
    features: [
      t("resale-agreements.offers.cards.edf-oa-2.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.edf-oa-2.eligibility.system-power"),
      t(
        "resale-agreements.offers.cards.edf-oa-2.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.edf-oa-2.eligibility.requires-rge-installer"
      ),
    ],
    category: "EDF_OA_S24",
    //prices: offerPrices.value.filter((o) =>
    //  o.categories.includes("EDF_OA_S24")
    //),
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.my-light-2.title"),
    provider: t("resale-agreements.offers.cards.my-light-2.provider"),
    logo: "https://images.ctfassets.net/1b6f0wuf6ave/5fZKjS7uSyIDjyqixCWsmL/0a269dc0c6b69d496f5e281d3353231f/logoWithText.a9bc32bd__1_.svg",
    id: "my-light-2",
    to: { name: "MyLight150MyBattery" },
    price: t("resale-agreements.offers.cards.my-light-2.price"),
    priceType: t("resale-agreements.offers.cards.my-light-2.price-type"),
    startingFees: t("resale-agreements.offers.cards.my-light-2.starting-fees"),
    additionalFees: t(
      "resale-agreements.offers.cards.my-light-2.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.my-light-2.description"),
    features: [
      t(
        "resale-agreements.offers.cards.my-light-2.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.my-light-2.eligibility.subscribed-power"
      ),
      t(
        "resale-agreements.offers.cards.my-light-2.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.my-light-2.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "VIRTUAL_BATTERY",
  },
  {
    name: t("resale-agreements.offers.cards.my-light-1.title"),
    provider: t("resale-agreements.offers.cards.my-light-1.provider"),
    logo: "https://images.ctfassets.net/1b6f0wuf6ave/5fZKjS7uSyIDjyqixCWsmL/0a269dc0c6b69d496f5e281d3353231f/logoWithText.a9bc32bd__1_.svg",
    id: "my-light-1",
    to: { name: "MyLight150MySmartBattery" },
    price: t("resale-agreements.offers.cards.my-light-1.price"),
    priceType: t("resale-agreements.offers.cards.my-light-1.price-type"),
    startingFees: t("resale-agreements.offers.cards.my-light-1.starting-fees"),
    additionalFees: t(
      "resale-agreements.offers.cards.my-light-1.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.my-light-1.description"),
    features: [
      t(
        "resale-agreements.offers.cards.my-light-1.eligibility.electricity-grid"
      ),
      t("resale-agreements.offers.cards.my-light-1.eligibility.system-power"),
      t(
        "resale-agreements.offers.cards.my-light-1.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.my-light-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "VIRTUAL_BATTERY",
  },
  {
    name: t("resale-agreements.offers.cards.urban-solar-3.title"),
    provider: t("resale-agreements.offers.cards.urban-solar-3.provider"),
    logo: "https://www.mapetiteenergie.com/wp-content/uploads/2021/02/LOGO_PNG_RVB.png",
    id: "urban-solar-3",
    to: { name: "UrbanSolarEnergyBatteryIndividuals" },
    price: t("resale-agreements.offers.cards.urban-solar-3.price"),
    priceType: t("resale-agreements.offers.cards.urban-solar-3.price-type"),
    startingFees: t(
      "resale-agreements.offers.cards.urban-solar-3.starting-fees"
    ),
    additionalFees: t(
      "resale-agreements.offers.cards.urban-solar-3.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.urban-solar-3.description"),
    features: [
      t(
        "resale-agreements.offers.cards.urban-solar-3.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-3.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-3.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-3.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "VIRTUAL_BATTERY",
  },
  {
    name: t("resale-agreements.offers.cards.urban-solar-4.title"),
    provider: t("resale-agreements.offers.cards.urban-solar-4.provider"),
    logo: "https://www.mapetiteenergie.com/wp-content/uploads/2021/02/LOGO_PNG_RVB.png",
    id: "urban-solar-4",
    to: { name: "UrbanSolarEnergyBatteryVirtualProfessional" },
    price: t("resale-agreements.offers.cards.urban-solar-4.price"),
    priceType: t("resale-agreements.offers.cards.urban-solar-4.price-type"),
    startingFees: t(
      "resale-agreements.offers.cards.urban-solar-4.starting-fees"
    ),
    additionalFees: t(
      "resale-agreements.offers.cards.urban-solar-4.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.urban-solar-4.description"),
    features: [
      t(
        "resale-agreements.offers.cards.urban-solar-4.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-4.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-4.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-4.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "VIRTUAL_BATTERY",
  },
  {
    name: t("resale-agreements.offers.cards.my-solar-kit.title"),
    provider: t("resale-agreements.offers.cards.my-solar-kit.provider"),
    logo: "https://images.ctfassets.net/1b6f0wuf6ave/6q1BqY5KfSxtS7Ryxw0Rwr/a8e4e820444a78c22432c30bd718a194/Group_3753.png",
    id: "my-solar-kit",
    to: { name: "MKSurplusWithValidate" },
    price: t("resale-agreements.offers.cards.my-solar-kit.price"),
    description: t("resale-agreements.offers.cards.my-solar-kit.description"),
    features: [
      t(
        "resale-agreements.offers.cards.my-solar-kit.eligibility.electricity-grid"
      ),
      t("resale-agreements.offers.cards.my-solar-kit.eligibility.system-power"),
      t(
        "resale-agreements.offers.cards.my-solar-kit.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.my-solar-kit.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.my-solar-kit-2.title"),
    provider: t("resale-agreements.offers.cards.my-solar-kit-2.provider"),
    logo: "https://images.ctfassets.net/1b6f0wuf6ave/6q1BqY5KfSxtS7Ryxw0Rwr/a8e4e820444a78c22432c30bd718a194/Group_3753.png",
    id: "my-solar-kit-2",
    to: { name: "MKSurplusWithoutValidate" },
    price: t("resale-agreements.offers.cards.my-solar-kit-2.price"),
    description: t("resale-agreements.offers.cards.my-solar-kit-2.description"),
    features: [
      t(
        "resale-agreements.offers.cards.my-solar-kit-2.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.my-solar-kit-2.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.my-solar-kit-2.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.my-solar-kit-2.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.3erl-1.title"),
    provider: t("resale-agreements.offers.cards.3erl-1.provider"),
    logo: "https://3erl.fr/wp-content/uploads/2024/02/logo-transparent.png",
    id: "edf-oa",
    to: { name: "3ERL" },
    price: t("resale-agreements.offers.cards.3erl-1.price"),
    priceType: t("resale-agreements.offers.cards.3erl-1.price-type"),
    startingFees: t("resale-agreements.offers.cards.3erl-1.starting-fees"),
    additionalFees: t("resale-agreements.offers.cards.3erl-1.additional-fees"),
    description: t("resale-agreements.offers.cards.3erl-1.description"),
    features: [
      t("resale-agreements.offers.cards.3erl-1.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.3erl-1.eligibility.system-power"),
      t("resale-agreements.offers.cards.3erl-1.eligibility.consumption-client"),
      t(
        "resale-agreements.offers.cards.3erl-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.kwatts.title"),
    provider: t("resale-agreements.offers.cards.kwatts.provider"),
    logo: "https://images.ctfassets.net/1b6f0wuf6ave/62epn9QYmD3mUIxDV29gNp/0f116fefc19a159d86c1f7f109e0293e/kwatts-logo-horizontal.c1a3fa06.webp",
    id: "kwatts",
    to: { name: "Kwatts" },
    price: t("resale-agreements.offers.cards.kwatts.price"),
    description: t("resale-agreements.offers.cards.kwatts.description"),
    features: [
      t("resale-agreements.offers.cards.kwatts.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.kwatts.eligibility.system-power"),
      t("resale-agreements.offers.cards.kwatts.eligibility.consumption-client"),
      t(
        "resale-agreements.offers.cards.kwatts.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.jpme-e-battery.title"),
    provider: t("resale-agreements.offers.cards.jpme-e-battery.provider"),
    logo: "https://static.iquesta.com/logo/iquesta/company/logo/10476.webp",
    id: "jpme-e-battery",
    to: { name: "JPMEEbatteryIndividualsLessThan15KVA" },
    price: t("resale-agreements.offers.cards.jpme-e-battery.price"),
    description: t("resale-agreements.offers.cards.jpme-e-battery.description"),
    features: [
      t(
        "resale-agreements.offers.cards.jpme-e-battery.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.jpme-e-battery.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.jpme-e-battery.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.jpme-e-battery.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "VIRTUAL_BATTERY",
  },
  {
    name: t("resale-agreements.offers.cards.jpme-e-battery-2.title"),
    provider: t("resale-agreements.offers.cards.jpme-e-battery-2.provider"),
    logo: "https://static.iquesta.com/logo/iquesta/company/logo/10476.webp",
    id: "jpme-e-battery-2",
    to: { name: "JPMEEbatteryIndividualsThreePhase" },
    price: t("resale-agreements.offers.cards.jpme-e-battery-2.price"),
    description: t(
      "resale-agreements.offers.cards.jpme-e-battery-2.description"
    ),
    features: [
      t(
        "resale-agreements.offers.cards.jpme-e-battery-2.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.jpme-e-battery-2.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.jpme-e-battery-2.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.jpme-e-battery-2.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "VIRTUAL_BATTERY",
  },
  {
    name: t("resale-agreements.offers.cards.urban-solar-1.title"),
    provider: t("resale-agreements.offers.cards.urban-solar-1.provider"),
    logo: "https://www.mapetiteenergie.com/wp-content/uploads/2021/02/LOGO_PNG_RVB.png",
    id: "urban-solar-1",
    to: { name: "UrbanSolarEnergyBoostProductionLessThan36kva" },
    price: t("resale-agreements.offers.cards.urban-solar-1.price"),
    priceType: t("resale-agreements.offers.cards.urban-solar-1.price-type"),
    startingFees: t(
      "resale-agreements.offers.cards.urban-solar-1.starting-fees"
    ),
    additionalFees: t(
      "resale-agreements.offers.cards.urban-solar-1.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.urban-solar-1.description"),
    features: [
      t(
        "resale-agreements.offers.cards.urban-solar-1.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-1.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-1.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.urban-solar-2.title"),
    provider: t("resale-agreements.offers.cards.urban-solar-2.provider"),
    logo: "https://www.mapetiteenergie.com/wp-content/uploads/2021/02/LOGO_PNG_RVB.png",
    id: "urban-solar-2",
    to: { name: "UrbanSolarEnergyBoostProductionPlus36kva" },
    price: t("resale-agreements.offers.cards.urban-solar-2.price"),
    priceType: t("resale-agreements.offers.cards.urban-solar-2.price-type"),
    startingFees: t(
      "resale-agreements.offers.cards.urban-solar-2.starting-fees"
    ),
    additionalFees: t(
      "resale-agreements.offers.cards.urban-solar-2.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.urban-solar-2.description"),
    features: [
      t(
        "resale-agreements.offers.cards.urban-solar-2.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-2.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-2.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.urban-solar-2.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.ekwateur-1.title"),
    provider: t("resale-agreements.offers.cards.ekwateur-1.provider"),
    logo: "https://upload.wikimedia.org/wikipedia/fr/b/b2/Ekwateur_Logo_RVB_Couleurs_Fond_Sombre.png",
    id: "ekwateur-1",
    to: { name: "EkwateurSurplusWithContract" },
    price: t("resale-agreements.offers.cards.ekwateur-1.price"),
    priceType: t("resale-agreements.offers.cards.ekwateur-1.price-type"),
    startingFees: t("resale-agreements.offers.cards.ekwateur-1.starting-fees"),
    additionalFees: t(
      "resale-agreements.offers.cards.ekwateur-1.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.ekwateur-1.description"),
    features: [
      t(
        "resale-agreements.offers.cards.ekwateur-1.eligibility.electricity-grid"
      ),
      t("resale-agreements.offers.cards.ekwateur-1.eligibility.system-power"),
      t(
        "resale-agreements.offers.cards.ekwateur-1.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.ekwateur-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.ekwateur-2.title"),
    provider: t("resale-agreements.offers.cards.ekwateur-2.provider"),
    logo: "https://upload.wikimedia.org/wikipedia/fr/b/b2/Ekwateur_Logo_RVB_Couleurs_Fond_Sombre.png",
    id: "ekwateur-2",
    to: { name: "EkwateurSurplusWithoutContract" },
    price: t("resale-agreements.offers.cards.ekwateur-2.price"),
    priceType: t("resale-agreements.offers.cards.ekwateur-2.price-type"),
    startingFees: t("resale-agreements.offers.cards.ekwateur-2.starting-fees"),
    additionalFees: t(
      "resale-agreements.offers.cards.ekwateur-2.additional-fees"
    ),
    description: t("resale-agreements.offers.cards.ekwateur-2.description"),
    features: [
      t(
        "resale-agreements.offers.cards.ekwateur-2.eligibility.electricity-grid"
      ),
      t("resale-agreements.offers.cards.ekwateur-2.eligibility.system-power"),
      t(
        "resale-agreements.offers.cards.ekwateur-2.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.ekwateur-2.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.jpme-1.title"),
    provider: t("resale-agreements.offers.cards.jpme-1.provider"),
    logo: "https://static.iquesta.com/logo/iquesta/company/logo/10476.webp",
    id: "jpme-1",
    to: { name: "JPMEEsurplusFixedForIndividuals" },
    price: t("resale-agreements.offers.cards.jpme-1.price"),
    priceType: t("resale-agreements.offers.cards.jpme-1.price-type"),
    startingFees: t("resale-agreements.offers.cards.jpme-1.starting-fees"),
    additionalFees: t("resale-agreements.offers.cards.jpme-1.additional-fees"),
    description: t("resale-agreements.offers.cards.jpme-1.description"),
    features: [
      t("resale-agreements.offers.cards.jpme-1.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.jpme-1.eligibility.system-power"),
      t("resale-agreements.offers.cards.jpme-1.eligibility.consumption-client"),
      t(
        "resale-agreements.offers.cards.jpme-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.jpme-2.title"),
    provider: t("resale-agreements.offers.cards.jpme-2.provider"),
    logo: "https://static.iquesta.com/logo/iquesta/company/logo/10476.webp",
    id: "jpme-2",
    to: { name: "JPMEEsurplusFlexForIndividuals" },
    price: t("resale-agreements.offers.cards.jpme-2.price"),
    priceType: t("resale-agreements.offers.cards.jpme-2.price-type"),
    startingFees: t("resale-agreements.offers.cards.jpme-2.starting-fees"),
    additionalFees: t("resale-agreements.offers.cards.jpme-2.additional-fees"),
    description: t("resale-agreements.offers.cards.jpme-2.description"),
    features: [
      t("resale-agreements.offers.cards.jpme-2.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.jpme-2.eligibility.system-power"),
      t("resale-agreements.offers.cards.jpme-2.eligibility.consumption-client"),
      t(
        "resale-agreements.offers.cards.jpme-2.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.jpme-3.title"),
    provider: t("resale-agreements.offers.cards.jpme-3.provider"),
    logo: "https://static.iquesta.com/logo/iquesta/company/logo/10476.webp",
    id: "jpme-3",
    to: { name: "JPMEEsurplusFixedForProfessionals" },
    price: t("resale-agreements.offers.cards.jpme-3.price"),
    priceType: t("resale-agreements.offers.cards.jpme-3.price-type"),
    startingFees: t("resale-agreements.offers.cards.jpme-3.starting-fees"),
    additionalFees: t("resale-agreements.offers.cards.jpme-3.additional-fees"),
    description: t("resale-agreements.offers.cards.jpme-3.description"),
    features: [
      t("resale-agreements.offers.cards.jpme-3.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.jpme-3.eligibility.system-power"),
      t("resale-agreements.offers.cards.jpme-3.eligibility.consumption-client"),
      t(
        "resale-agreements.offers.cards.jpme-3.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.jpme-4.title"),
    provider: t("resale-agreements.offers.cards.jpme-4.provider"),
    logo: "https://static.iquesta.com/logo/iquesta/company/logo/10476.webp",
    id: "jpme-4",
    to: { name: "JPMEEsurplusFlexForProfessionals" },
    price: t("resale-agreements.offers.cards.jpme-4.price"),
    priceType: t("resale-agreements.offers.cards.jpme-4.price-type"),
    startingFees: t("resale-agreements.offers.cards.jpme-4.starting-fees"),
    additionalFees: t("resale-agreements.offers.cards.jpme-4.additional-fees"),
    description: t("resale-agreements.offers.cards.jpme-4.description"),
    features: [
      t("resale-agreements.offers.cards.jpme-4.eligibility.electricity-grid"),
      t("resale-agreements.offers.cards.jpme-4.eligibility.system-power"),
      t("resale-agreements.offers.cards.jpme-4.eligibility.consumption-client"),
      t(
        "resale-agreements.offers.cards.jpme-4.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.octopus-energy-1.title"),
    provider: t("resale-agreements.offers.cards.octopus-energy-1.provider"),
    logo: "https://upload.wikimedia.org/wikipedia/commons/9/97/OE_Logo_Centered_Dark_with_Constantine.jpg",
    id: "octopus-energy-1",
    to: { name: "OctopusEnergyPurchaseContractForIndividuals" },
    price: t("resale-agreements.offers.cards.octopus-energy-1.price"),
    priceType: t("resale-agreements.offers.cards.octopus-energy-1.price-type"),
    startingFees: t(
      "resale-agreements.offers.cards.octopus-energy-1.starting-fees"
    ),
    additionalFees: t(
      "resale-agreements.offers.cards.octopus-energy-1.additional-fees"
    ),
    description: t(
      "resale-agreements.offers.cards.octopus-energy-1.description"
    ),
    features: [
      t(
        "resale-agreements.offers.cards.octopus-energy-1.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.octopus-energy-1.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.octopus-energy-1.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.octopus-energy-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.octopus-energy-2.title"),
    provider: t("resale-agreements.offers.cards.octopus-energy-2.provider"),
    logo: "https://upload.wikimedia.org/wikipedia/commons/9/97/OE_Logo_Centered_Dark_with_Constantine.jpg",
    id: "octopus-energy-2",
    to: "#",
    price: t("resale-agreements.offers.cards.octopus-energy-2.price"),
    priceType: t("resale-agreements.offers.cards.octopus-energy-1.price-type"),
    startingFees: t(
      "resale-agreements.offers.cards.octopus-energy-1.starting-fees"
    ),
    additionalFees: t(
      "resale-agreements.offers.cards.octopus-energy-1.additional-fees"
    ),
    description: t(
      "resale-agreements.offers.cards.octopus-energy-2.description"
    ),
    features: [
      t(
        "resale-agreements.offers.cards.octopus-energy-2.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.octopus-energy-2.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.octopus-energy-2.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.octopus-energy-2.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.mint-energie-1.title"),
    provider: t("resale-agreements.offers.cards.mint-energie-1.provider"),
    logo: "https://www.mint-energie.com/App_Themes/Theme1/Images/mint-energie-logo.png?v=1",
    id: "mint-energie-1",
    to: "#",
    price: t("resale-agreements.offers.cards.mint-energie-1.price"),
    description: t("resale-agreements.offers.cards.mint-energie-1.description"),
    features: [
      t(
        "resale-agreements.offers.cards.mint-energie-1.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.mint-energie-1.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.mint-energie-1.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.mint-energie-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
  {
    name: t("resale-agreements.offers.cards.total-energies-1.title"),
    provider: t("resale-agreements.offers.cards.total-energies-1.provider"),
    logo: "https://d11o8pt3cttu38.cloudfront.net/wp-content/uploads/sites/6/2021/06/logo_totalenergies-vertical.svg",
    id: "total-energies-1",
    to: { name: "ProjectPostInstallationResaleAgreementTotalEnergies" },
    price: t("resale-agreements.offers.cards.total-energies-1.price"),
    priceType: t("resale-agreements.offers.cards.total-energies-1.price-type"),
    startingFees: t(
      "resale-agreements.offers.cards.total-energies-1.starting-fees"
    ),
    additionalFees: t(
      "resale-agreements.offers.cards.total-energies-1.additional-fees"
    ),
    description: t(
      "resale-agreements.offers.cards.total-energies-1.description"
    ),
    features: [
      t(
        "resale-agreements.offers.cards.total-energies-1.eligibility.electricity-grid"
      ),
      t(
        "resale-agreements.offers.cards.total-energies-1.eligibility.system-power"
      ),
      t(
        "resale-agreements.offers.cards.total-energies-1.eligibility.consumption-client"
      ),
      t(
        "resale-agreements.offers.cards.total-energies-1.eligibility.requires-rge-installer"
      ),
    ],
    mostPopular: false,
    type: "FEED_IN_TARIFF",
  },
])

const type = ref(types[0])
const filteredOffers = computed(() =>
  type.value.value === "all"
    ? offers.value
    : offers.value.filter((o) => o.type === type.value.value)
)

const onHandleClick = (offer) => {
  router.push(offer.to)
}
</script>

<template>
  <div class="mx-auto max-w-7xl px-6 lg:px-8 py-12 sm:py-16 offers">
    <div class="">
      <div class="flex justify-end">
        <fieldset aria-label="Contract type">
          <RadioGroup
            v-model="type"
            class="flex gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200"
          >
            <RadioGroupOption
              v-for="item in types"
              :key="item.value"
              v-slot="{ checked }"
              as="template"
              :value="item"
            >
              <div
                :class="[
                  checked ? 'bg-orange-500 text-white' : 'text-gray-500',
                  'cursor-pointer rounded-full px-2 py-1',
                ]"
              >
                {{ item.label }}
              </div>
            </RadioGroupOption>
          </RadioGroup>
        </fieldset>
      </div>
      <div
        class="isolate flex flex-row gap-x-3 w-full overflow-x-scroll py-5 md:px-1 scrollbar"
      >
        <OfferCard
          v-for="offer in filteredOffers"
          :key="offer.id"
          :offer="offer"
          class="flex-none ring-1 ring-gray-200 rounded-3xl p-8 w-[350px] cursor-pointer"
          @click="onHandleClick(offer)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.scrollbar::-webkit-scrollbar {
  height: 7px;
}
.scrollbar::-webkit-scrollbar-track {
  background-color: whitesmoke;
}
::-webkit-scrollbar-thumb {
  background-color: #ff7b2f;
  border-radius: 5px;
}
</style>
